import React from "react";
import { Button, Grid, Box } from "@material-ui/core";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { CRadioGroup, CSelect, CTextField, HFlex, Text, Icon } from "shared";
import { Area } from "types";
import { ALL_ROLES_VALUE, Category, ROLES, Vars as Props } from "../queries/useListUsers";
import { countries, CountryCode, getCountryCode, isUsMarket } from "utils/market";
import { Breakpoint } from "theme";
import { useWindowWidth } from "utils/hooks";
import { Search } from "@material-ui/icons";
import { join } from "utils/misc";

const countryItems = countries.map(c => ({ ...c, value: getCountryCode(c.value) }));
const defaultCountryCode = countryItems[0].value;

export const SearchUserForm: React.VFC<Props> = ({ category, search, role, country_code }) => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const isNarrow = useWindowWidth() < Breakpoint.sm;
  const form = useForm();
  const currentCategory = form.watch("category", category);
  const currentSearch = form.watch("search", search);
  const currentRole = form.watch("role", role);

  const categories = [
    {
      value: Category.q,
      label: t("form.name_or_email"),
      placeholder: t("form.name_or_email"),
    },
    {
      value: Category.id,
      label: t("patients.id"),
      placeholder: t("patients.id"),
      pattern: /\d+/,
    },
    {
      value: Category.uid,
      label: "UID",
      placeholder: "UID",
    },
    {
      value: Category.phone,
      label: t("form.phone"),
      placeholder: t("form.phone"),
    },
    {
      value: Category.nhs,
      label: t("form.nhs_number"),
      placeholder: t("form.nhs_number"),
    },
  ];

  const selectedCategory = categories.find(c => c.value === currentCategory) ?? categories[0];

  const submit = form.handleSubmit(fields => {
    const country_code = isUsMarket ? CountryCode.US : fields.country_code;
    const params = new URLSearchParams({ ...fields, ...(country_code ? { country_code } : {}) });
    push(join(Area.Users, params));
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={submit}>
        <HFlex mb={4} justifyContent="center" alignItems="baseline" pre>
          <Text xlarge light>
            {t("common.find")}{" "}
          </Text>
          <CSelect name="role" items={[...ROLES]} defaultValue={role || "patient"} variant="standard">
            {({ label }) => (
              <Text xlarge light>
                {label}
              </Text>
            )}
          </CSelect>
          <Text xlarge light>
            {" "}
            {t("common.in")}{" "}
          </Text>
          {isUsMarket ? (
            <HFlex alignItems="center">
              <Text xlarge light mr={2}>
                {countryItems[0].name}
              </Text>
              <Icon ml="auto" size={3} of={countryItems[0].Flag} />
            </HFlex>
          ) : (
            <CSelect
              name="country_code"
              items={countryItems}
              defaultValue={country_code || defaultCountryCode}
              variant="standard"
            >
              {({ name, Flag }) => (
                <HFlex alignItems="center">
                  <Text xlarge light mr={2}>
                    {name}
                  </Text>
                  <Icon ml="auto" size={3} of={Flag} />
                </HFlex>
              )}
            </CSelect>
          )}
        </HFlex>
        <Box mb={1}>
          {isNarrow ? (
            <HFlex mb={2} alignItems="baseline" pre>
              <Text>{t("form.by")} </Text>
              <CSelect name="category" items={categories} defaultValue={currentCategory} variant="standard">
                {({ label }) => <Text initial>{label}</Text>}
              </CSelect>
            </HFlex>
          ) : (
            <CRadioGroup
              label={t("form.by_category")}
              row
              items={categories}
              name="category"
              defaultValue={currentCategory}
              aria-label="Categories"
            />
          )}
        </Box>
        <Grid container alignItems="flex-start" spacing={3}>
          <Grid item xs={12} sm>
            <CTextField
              fullWidth
              name="search"
              adornment={<Icon of={Search} size={1.5} />}
              placeholder={isNarrow ? "" : selectedCategory.placeholder}
              defaultValue={search}
              pattern={selectedCategory.pattern}
              aria-label="Search string"
              required={!currentRole || currentRole === ALL_ROLES_VALUE}
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              disabled={!currentSearch && (!currentRole || currentRole === ALL_ROLES_VALUE)}
              aria-label="Search"
            >
              {t("buttons.search")}
            </Button>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};
